import { ClientEnvironment } from "~/environment/ClientEnvironment";
import * as actions from "libs/actions";
import { toast } from "~/environment/toast-service";
import { ApiInput } from "libs/ApiTypes";
import { withActionContext, write } from "./write";
import { InvalidActionStateError } from "libs/actions";

export type InviteUsersParams = ApiInput<"inviteUsers">;

export const inviteUsers = async function (environment: ClientEnvironment, params: InviteUsersParams) {
  using disposable = environment.isLoading.add();

  const response = await environment.api.inviteUsers(params);

  if (response.status !== 200) return;

  environment.writeRecordMap(response.body.recordMap);
};

export const revokeUserInvitation = withActionContext(
  async (
    context,
    environment,
    props: {
      /** Invitation id */
      id: string;
      email_address: string;
    },
  ) => {
    const [record] = await context.environment.loader.getRecord("organization_user_invitation", props.id);

    if (!record) {
      throw new InvalidActionStateError("[revokeUserInvitation] Invitation not found", {
        errorData: { id: props.id },
      });
    }

    if (record.expires_at) {
      throw new InvalidActionStateError("[revokeUserInvitation] Invitation already expired", {
        errorData: { id: props.id },
      });
    }

    return {
      forward: async () => {
        using disposable = environment.isLoading.add();

        const isActionConfirmed = confirm(`Are you sure you want to revoke the invitation for ${props.email_address}?`);

        if (!isActionConfirmed) {
          throw new InvalidActionStateError("[revokeUserInvitation] Cancelled by user", {
            userMessage: "SUPPRESS",
          });
        }

        const action = await actions.invitations.updateUserInvitation.action(
          { invitationId: props.id, expiresAt: "now" },
          context,
        );

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: "Revoking invitation...",
            });
          },
        });

        toast("vanilla", {
          subject: "Invitation revoked.",
        });
      },
      backward: async () => {
        using disposable = environment.isLoading.add();

        const action = await actions.invitations.updateUserInvitation.action(
          { invitationId: props.id, expiresAt: record.expires_at },
          context,
        );

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: "Restoring invitation...",
            });
          },
        });

        toast("vanilla", {
          subject: "Invitation restored.",
        });
      },
    };
  },
);

export const deleteUserInvitation = withActionContext(
  async (
    context,
    environment,
    props: {
      /** Invitation id */
      id: string;
    },
  ) => {
    return {
      forward: async () => {
        using disposable = environment.isLoading.add();

        const action = await actions.invitations.deleteUserInvitation.action({ invitationId: props.id }, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: "Invitation deleted.",
            });
          },
        });
      },
    };
  },
);
